import {
  Snackbar,
  ThemeProvider,
  BrandedSplash,
  defaultSettings,
  SettingsProvider,
  useOAuthCallback,
  LocalizationProvider,
} from '@nexus/ari-lib';
import { AuthGuard, useFederatedLoginGuard } from '@nexus/supabase';
import { RouterProvider } from '@tanstack/react-router';
import { MotionConfig } from 'framer-motion';

import { CssBaseline } from '@mui/material';

import { useAuth } from '../hooks/use-auth';
import ariCloudLogo from '../assets/ari-cloud-logo.svg';
import { getSubscriptionManagerUrl } from '../config/global';
import { AriI18nProvider } from '../lib/ari-i18n-provider';
import { USER_WORKSPACES_QUERY_KEY } from '../hooks/use-workspaces';
import { useSyncCountryWithLanguage } from '../hooks/use-locale-sync';
import { useWorkspaceSwitchGuard } from '../hooks/use-workspace-switch-guard';
import { WorkspaceSwitchBlockedDialog } from '../components/workspace-switch-blocked-dialog';
import type { createAppRouter } from '../routes';

export function App({ router }: { router: ReturnType<typeof createAppRouter> }) {
  useSyncCountryWithLanguage();
  useOAuthCallback();
  const { isAuthenticated, isLoading, user, session, signOut } = useAuth();
  const { blockedSwitch, isBlockingSwitch, dismissBlockedSwitch } = useWorkspaceSwitchGuard({
    email: user?.email,
    isLoading,
    signOut,
  });
  const { isRevoking } = useFederatedLoginGuard({
    accessToken: session?.access_token,
    isAuthenticated,
    isAuthLoading: isLoading,
    signOut,
    baseUrl: getSubscriptionManagerUrl(),
    queryKey: USER_WORKSPACES_QUERY_KEY,
  });
  const shouldBlockProtectedContent = isBlockingSwitch || Boolean(blockedSwitch) || isRevoking;

  return (
    <AriI18nProvider>
      <SettingsProvider defaultSettings={defaultSettings}>
        <LocalizationProvider>
          <ThemeProvider defaultMode="dark">
            <CssBaseline />
            <MotionConfig reducedMotion="user">
              {/* A refused workspace switch holds the other account's session, so keep
                  the app unmounted until the sign-out redirect lands. See CLOUD-715. */}
              {shouldBlockProtectedContent ? (
                <BrandedSplash logoSrc={ariCloudLogo} />
              ) : (
                <AuthGuard
                  isLoading={isLoading}
                  isAuthenticated={isAuthenticated}
                  fallback={<BrandedSplash logoSrc={ariCloudLogo} />}
                >
                  <RouterProvider router={router} context={{ auth: { isAuthenticated } }} />
                </AuthGuard>
              )}
              <WorkspaceSwitchBlockedDialog blockedSwitch={blockedSwitch} onClose={dismissBlockedSwitch} />
              <Snackbar />
            </MotionConfig>
          </ThemeProvider>
        </LocalizationProvider>
      </SettingsProvider>
    </AriI18nProvider>
  );
}

export default App;
